import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CircularProgress } from '@mui/material';
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ZoomIn } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import { systemConfig } from '@/config/systemConfig';
import { ROUTES } from '@/route/routes.constants';
import { Inspection } from '@/types/Inspection.type';
import {
  FactoryVisitSession,
  LocationEvidence,
  LocationEvidenceRole,
  LocationVerificationDraft,
} from '@/types/LocationVerification.type';
import {
  isFactoryVerificationEnabled,
  getMinGpsAccuracyMeters,
} from '@/utils/factoryVerificationConfig.util';
import { useAppSelector } from '@/store/hooks';
import { RootState } from '@/store/rootReducer';
import { useNetwork } from '@/hooks/useNetwork';
import { useSavedInspection } from '@/hooks/useSavedInspection';
import { useLocationVerificationDraft } from '@/hooks/useLocationVerificationDraft';
import { loadInspectionForInspectApp } from '@/utils/ensureInspectionHydrated';
import store from '@/store/store';
import { captureBestDeviceLocation, DeviceLocationFix } from '@/utils/captureBestDeviceLocation';
import {
  resolvePhotoEvidenceLocation,
  startPhotoGpsCapture,
} from '@/utils/factoryVerificationPhotoCapture.util';
import {
  GPS_RETRY_MAX_ATTEMPTS,
  GPS_RETRY_TIMEOUT_MS,
  GpsRetryState,
  INITIAL_GPS_RETRY_STATE,
  isDeviceLocationFresh,
  pickBestDeviceLocationFix,
  runGpsRetrySequence,
} from '@/utils/factoryVerificationGps.util';
import { resolveServerFactoryVisitSessionId } from '@/utils/locationVerification.utils';
import { generateUniqueId } from '@/utils/string.util';
import { isImageSupported } from '@/utils/image.util';
import { locationVerificationDraftStorage } from '@/store/idbStorage/locationVerificationDraftStorage';
import {
  buildFactoryVisitSessionLocalId,
  factoryVisitSessionStorage,
} from '@/store/idbStorage/factoryVisitSessionStorage';
import { fetchActiveFactoryVisitSession } from '@/utils/factoryVisitSession.api';
import { toLocalDateString } from '@/utils/factoryVisitSession.util';
import { getInspectorTimeZone } from '@/utils/inspectorTimeZone.util';
import {
  applyGpsFixToEvidence,
  getEvidenceForRole,
  hasEvidenceForRole,
  hasUnsavedEntranceProgress,
  inspectionRequiresLocationVerification,
  isVerificationProgressCommitted,
  locationEvidenceHasGpsCoordinates,
  resolveVerificationStep,
  shouldInvalidateVerificationDraft,
} from '@/utils/locationVerification.utils';
import { completeVerificationAndStart } from '@/utils/locationVerificationStart';
import {
  logFactoryVerificationDebug,
  logFactoryVerificationError,
} from '@/utils/factoryVerificationLogger.util';
import { InspectionLocationMap } from '@/components/maps/InspectionLocationMap';
import { FactoryVerificationStepRail } from '@/features/inspection-list/components/FactoryVerificationStepRail';
import {
  isFactoryVerificationUserStepOne,
  resolveUserStepStates,
} from '@/utils/factoryVerificationStepRail.util';
import {
  buildFactoryVerificationPhotoGridItems,
  countPendingCapturesByRole,
  PendingVerificationCapture,
} from '@/utils/factoryVerificationPhotoGrid.util';
import {
  shouldAdvanceToRoomOnSave,
  shouldShowRoomStepBackButton,
  shouldShowVerificationMap,
  shouldShowVerificationMapPlaceholder,
} from '@/utils/factoryVerificationNavigation.util';
import { LocationCaptureMetaBar } from '@/features/inspection-list/components/LocationCaptureMetaBar';
import { VerificationMapExpandOverlay } from '@/features/inspection-list/components/VerificationMapExpandOverlay';
import {
  ImageZoomLightbox,
  ImageZoomSlide,
} from '@/features/components/ImageCard/ImageZoomLightbox';
import { InspectionImageType } from '@/enums/InspectionImage.enum';
import { Image } from '@/types/Image.type';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { ROLE } from '@/role.constant';
import styles from './FactoryVerificationPage.module.scss';
import actionStyles from './styles/inspectionActionButtons.module.scss';

type DeviceLocation = DeviceLocationFix;

type PendingPhotoCaptureSession = {
  clientEvidenceId: string;
  role: LocationEvidenceRole;
  gpsPromise: Promise<DeviceLocationFix | null>;
};

const MAX_PHOTOS_PER_LOCATION = 3;
const PHOTO_GPS_MAX_WATCH_MS = 12_000;

type FactoryVerificationLoadingPhase = 'idle' | 'initialGps';

export const FactoryVerificationPage: FC = () => {
  const { id, step: stepParam } = useParams<{ id: string; step: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isOnline = useNetwork();
  const user = useAppSelector((state: RootState) => state.auth.user);
  const tenantId = systemConfig.tenantId;
  const factoryVerificationEnabled = useAppSelector((state: RootState) =>
    isFactoryVerificationEnabled(state.appConfig)
  );
  const minGpsAccuracyMeters = useAppSelector((state: RootState) =>
    getMinGpsAccuracyMeters(state.appConfig)
  );
  const cachedInspection =
    useAppSelector((state: RootState) =>
      state.inspectionsData?.inspections.find((inspection: Inspection) => inspection.id === id)
    ) || null;
  const { savedInspection, isLoading: isSavedInspectionLoading } = useSavedInspection(id);
  const { draft, isLoading: isDraftLoading, refreshDraft } = useLocationVerificationDraft(id);
  const { hasRole } = useRoleAccess();
  const [inspectionDetails, setInspectionDetails] = useState<Inspection | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deviceLocation, setDeviceLocation] = useState<DeviceLocation | null>(null);
  const [bestAttemptLocation, setBestAttemptLocation] = useState<DeviceLocation | null>(null);
  const [gpsRetryState, setGpsRetryState] = useState<GpsRetryState>(INITIAL_GPS_RETRY_STATE);
  const bestAttemptLocationRef = useRef<DeviceLocation | null>(null);
  const [loadingPhase, setLoadingPhase] = useState<FactoryVerificationLoadingPhase>('idle');
  const isGpsCaptureRoute = stepParam === 'entrance' || stepParam === 'room';
  const [hasCompletedInitialLocationRequest, setHasCompletedInitialLocationRequest] =
    useState(!isGpsCaptureRoute);
  const [isSaving, setIsSaving] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});
  const [factorySession, setFactorySession] = useState<FactoryVisitSession | null>(null);
  const [factorySessionReuseDays, setFactorySessionReuseDays] = useState<number>(1);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxInitialIndex, setLightboxInitialIndex] = useState(0);
  const [mapExpandOpen, setMapExpandOpen] = useState(false);
  const [pendingCaptures, setPendingCaptures] = useState<PendingVerificationCapture[]>([]);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const pendingCapturesRef = useRef<PendingVerificationCapture[]>([]);
  const captureSaveQueueRef = useRef(Promise.resolve());
  const pendingPhotoCaptureRef = useRef<PendingPhotoCaptureSession | null>(null);
  const step = resolveVerificationStep(stepParam, draft, factorySession, factorySessionReuseDays);

  useEffect(() => {
    pendingCapturesRef.current = pendingCaptures;
  }, [pendingCaptures]);

  useEffect(() => {
    bestAttemptLocationRef.current = bestAttemptLocation;
  }, [bestAttemptLocation]);

  useEffect(() => {
    let cancelled = false;
    const loadInspection = async () => {
      if (!id) {
        setErrorMessage(t('inspection_not_found'));
        setIsLoading(false);
        return;
      }
      if (isSavedInspectionLoading) {
        return;
      }
      setIsLoading(true);
      try {
        const result = await loadInspectionForInspectApp({
          inspectionId: id,
          isOnline,
          savedInspection: savedInspection ?? null,
          listRow: cachedInspection,
          dispatch: store.dispatch,
        });
        if (cancelled) {
          return;
        }
        if (!result.ok) {
          setErrorMessage(
            t(
              result.reason === 'offline_lite'
                ? 'inspection_hydration_requires_connection'
                : 'inspection_not_found'
            )
          );
          setInspectionDetails(null);
          return;
        }
        setInspectionDetails(result.inspection);
      } catch (error) {
        if (!cancelled) {
          Sentry.captureException(error);
          setErrorMessage(t('inspection_start_failed'));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    void loadInspection();
    return () => {
      cancelled = true;
    };
  }, [id, isOnline, isSavedInspectionLoading, savedInspection, cachedInspection?.id, t]);

  useEffect(() => {
    if (!id || !inspectionDetails) {
      return;
    }
    const verificationApplicable = inspectionRequiresLocationVerification(inspectionDetails);
    if (!verificationApplicable || !factoryVerificationEnabled) {
      navigate(ROUTES.INSPECTION_START.replace(':id', id), { replace: true });
    }
  }, [id, inspectionDetails, factoryVerificationEnabled, navigate]);

  useEffect(() => {
    if (!id || !inspectionDetails || !draft) {
      return;
    }
    if (!shouldInvalidateVerificationDraft(draft, inspectionDetails)) {
      return;
    }
    let cancelled = false;
    const resetDraft = async () => {
      await locationVerificationDraftStorage.remove(id);
      if (!cancelled) {
        await refreshDraft();
      }
    };
    void resetDraft();
    return () => {
      cancelled = true;
    };
  }, [id, inspectionDetails, draft, refreshDraft]);

  useEffect(() => {
    let cancelled = false;
    const loadFactorySession = async () => {
      if (!inspectionDetails?.factory?.id || !user?.id || !tenantId) {
        setFactorySession(null);
        setFactorySessionReuseDays(1);
        return;
      }
      const lookup = await fetchActiveFactoryVisitSession({
        factoryId: inspectionDetails.factory.id,
        tenantId,
        inspectorId: user.id,
        isOnline,
      });
      if (!cancelled) {
        setFactorySessionReuseDays(lookup.reuseDays);
        setFactorySession(lookup.session);
      }
    };
    void loadFactorySession();
    return () => {
      cancelled = true;
    };
  }, [inspectionDetails?.factory?.id, user?.id, tenantId, isOnline]);

  useEffect(() => {
    if (!id || stepParam !== 'session') {
      return;
    }
    navigate(ROUTES.INSPECTION_VERIFICATION.replace(':id', id).replace(':step', 'entrance'), {
      replace: true,
    });
  }, [id, stepParam, navigate]);

  useEffect(() => {
    if (!id || step !== 'saved' || !draft) {
      return;
    }
    if (hasUnsavedEntranceProgress(draft)) {
      navigate(ROUTES.INSPECTION_VERIFICATION.replace(':id', id).replace(':step', 'entrance'), {
        replace: true,
      });
    }
  }, [id, step, draft, navigate]);

  useEffect(() => {
    if (!id || step !== 'entrance' || !draft) {
      return;
    }
    if (
      hasEvidenceForRole(draft, 'ENTRANCE') &&
      isVerificationProgressCommitted(draft) &&
      !hasEvidenceForRole(draft, 'INSPECTION_ROOM')
    ) {
      navigate(ROUTES.INSPECTION_VERIFICATION.replace(':id', id).replace(':step', 'saved'), {
        replace: true,
      });
    }
  }, [id, step, draft, navigate]);

  useEffect(() => {
    if (!draft?.evidence?.length || !id) {
      setThumbnailUrls({});
      return;
    }
    let cancelled = false;
    const loadThumbnails = async () => {
      const visibleEvidence = draft.evidence.filter((evidence) => evidence.hasPhoto !== false);
      const entries = await Promise.all(
        visibleEvidence.map(async (evidence) => {
          const data =
            (await locationVerificationDraftStorage.getImageData(id, evidence.clientEvidenceId)) ??
            (draft.factoryId
              ? await factoryVisitSessionStorage.getSessionImageData(
                  draft.factoryId,
                  evidence.localImageKey
                )
              : null);
          if (data) {
            return [evidence.clientEvidenceId, data] as const;
          }
          if (evidence.imageUrl) {
            return [evidence.clientEvidenceId, evidence.imageUrl] as const;
          }
          return null;
        })
      );
      if (!cancelled) {
        const nextUrls: Record<string, string> = {};
        for (const entry of entries) {
          if (entry) {
            nextUrls[entry[0]] = entry[1];
          }
        }
        setThumbnailUrls(nextUrls);
      }
    };
    void loadThumbnails();
    return () => {
      cancelled = true;
    };
  }, [draft, id]);

  const entranceEvidence = useMemo(() => getEvidenceForRole(draft, 'ENTRANCE'), [draft]);
  const roomEvidence = useMemo(() => getEvidenceForRole(draft, 'INSPECTION_ROOM'), [draft]);

  const captureDeviceLocationFix = async (
    showToastOnFailure = false
  ): Promise<DeviceLocation | null> => {
    if (!navigator?.geolocation) {
      if (showToastOnFailure) {
        toast.warn(t('gps_coordinates_not_obtained_verification_required'));
      }
      return null;
    }
    const loc = await captureBestDeviceLocation({
      maxWatchMs: PHOTO_GPS_MAX_WATCH_MS,
      minAccuracyMetres: minGpsAccuracyMeters,
    });
    if (!loc) {
      if (showToastOnFailure) {
        toast.warn(t('gps_coordinates_not_obtained_verification_required'));
      }
      return null;
    }
    setDeviceLocation(loc);
    setBestAttemptLocation((current) => pickBestDeviceLocationFix(current, loc));
    return loc;
  };

  const captureRetryGpsFix = (): Promise<DeviceLocationFix | null> =>
    captureBestDeviceLocation({
      maxWatchMs: GPS_RETRY_TIMEOUT_MS,
      minAccuracyMetres: minGpsAccuracyMeters,
      maxFallbackAgeMs: 0,
    });

  const backfillEvidenceGpsForRole = async (
    role: LocationEvidenceRole,
    fix: DeviceLocationFix
  ): Promise<void> => {
    if (!id || !draft) {
      return;
    }
    const needsBackfill = getEvidenceForRole(draft, role).some(
      (item) => !locationEvidenceHasGpsCoordinates(item)
    );
    if (!needsBackfill) {
      return;
    }
    const nextEvidence = draft.evidence.map((item) => {
      if (item.role === role && !locationEvidenceHasGpsCoordinates(item)) {
        return applyGpsFixToEvidence(item, fix);
      }
      return item;
    });
    const nextDraft: LocationVerificationDraft = {
      ...draft,
      evidence: nextEvidence,
      updatedAt: new Date().toISOString(),
    };
    await locationVerificationDraftStorage.put(nextDraft);
    if (factorySession) {
      const nextSessionEvidence = factorySession.evidence.map((item) => {
        if (item.role === role && (item.latitude == null || item.longitude == null)) {
          return {
            ...item,
            latitude: fix.latitude,
            longitude: fix.longitude,
            accuracy: fix.accuracyMetres,
            altitude: fix.altitude,
            altitudeAccuracy: fix.altitudeAccuracy,
            heading: fix.heading,
            speed: fix.speed,
            gpsCaptureSource: fix.gpsCaptureSource,
            gpsAccuracyPreciseEnough: fix.gpsAccuracyPreciseEnough,
            connectionType: fix.connectionType,
            connectionEffectiveType: fix.connectionEffectiveType,
            geolocationPermissionState: fix.geolocationPermissionState,
            clientUserAgent: fix.clientUserAgent,
            gpsCapturedAt: fix.gpsCapturedAt,
          };
        }
        return item;
      });
      const nextSession: FactoryVisitSession = {
        ...factorySession,
        evidence: nextSessionEvidence,
        updatedAt: new Date().toISOString(),
      };
      await factoryVisitSessionStorage.put(nextSession);
      setFactorySession(nextSession);
    }
    await refreshDraft();
  };

  const applyGpsFixToState = (fix: DeviceLocationFix): void => {
    setDeviceLocation(fix);
    setBestAttemptLocation((current) => pickBestDeviceLocationFix(current, fix));
  };

  const handleRetryLocation = async (): Promise<void> => {
    if (gpsRetryState.phase === 'retrying') {
      return;
    }
    setGpsRetryState({
      phase: 'retrying',
      attempt: 1,
      secondsRemaining: Math.ceil(GPS_RETRY_TIMEOUT_MS / 1000),
    });
    const result = await runGpsRetrySequence({
      captureOnce: captureRetryGpsFix,
      minAccuracyMetres: minGpsAccuracyMeters,
      maxAttempts: GPS_RETRY_MAX_ATTEMPTS,
      timeoutMs: GPS_RETRY_TIMEOUT_MS,
      onAttemptTick: (attempt, secondsRemaining) => {
        setGpsRetryState({
          phase: 'retrying',
          attempt,
          secondsRemaining,
        });
      },
    });
    const resolvedFix = result.successFix ?? result.bestFix;
    if (result.successFix) {
      applyGpsFixToState(result.successFix);
      setGpsRetryState(INITIAL_GPS_RETRY_STATE);
      await backfillEvidenceGpsForRole(resolveCaptureRole(), result.successFix);
      return;
    }
    if (resolvedFix) {
      setBestAttemptLocation((current) => pickBestDeviceLocationFix(current, resolvedFix));
      setDeviceLocation(resolvedFix);
      await backfillEvidenceGpsForRole(resolveCaptureRole(), resolvedFix);
    }
    setGpsRetryState({
      phase: 'exhausted',
      attempt: GPS_RETRY_MAX_ATTEMPTS,
      secondsRemaining: 0,
    });
  };

  const resolvePhotoGpsFix = async (): Promise<DeviceLocationFix | null> => {
    if (isDeviceLocationFresh(deviceLocation)) {
      return deviceLocation;
    }
    const captured = await captureDeviceLocationFix(false);
    if (captured) {
      return captured;
    }
    return bestAttemptLocationRef.current;
  };

  useEffect(() => {
    if (step !== 'entrance' && step !== 'room') {
      setHasCompletedInitialLocationRequest(true);
      setLoadingPhase('idle');
      return;
    }
    let cancelled = false;
    setDeviceLocation(null);
    setBestAttemptLocation(null);
    setGpsRetryState(INITIAL_GPS_RETRY_STATE);
    setHasCompletedInitialLocationRequest(false);
    setLoadingPhase('initialGps');
    const loadInitialLocation = async () => {
      const loc = await captureDeviceLocationFix(false);
      if (!cancelled && loc) {
        setBestAttemptLocation(loc);
      }
      if (!cancelled) {
        setHasCompletedInitialLocationRequest(true);
        setLoadingPhase('idle');
      }
    };
    void loadInitialLocation();
    return () => {
      cancelled = true;
    };
  }, [step, id]);

  const ensureDraft = async (): Promise<LocationVerificationDraft> => {
    if (!id || !user?.id) {
      throw new Error('Missing inspection or user');
    }
    const existing = await locationVerificationDraftStorage.get(id);
    if (existing) {
      return existing;
    }
    const factoryId = inspectionDetails?.factory?.id;
    return {
      inspectionId: id,
      tenantId: tenantId || '',
      inspectorId: user.id,
      factoryId,
      workflowState: 'ENTRANCE_CAPTURED',
      evidence: [],
      observedStatusId: inspectionDetails?.inspectionStatusId,
      updatedAt: new Date().toISOString(),
    };
  };

  const ensureFactorySession = async (): Promise<FactoryVisitSession | null> => {
    if (!inspectionDetails?.factory?.id || !user?.id || !tenantId) {
      return null;
    }
    const inspectorTimeZone = getInspectorTimeZone();
    const sessionId = buildFactoryVisitSessionLocalId({
      tenantId,
      factoryId: inspectionDetails.factory.id,
      inspectorId: user.id,
      inspectorTimeZone,
    });
    const existing = await factoryVisitSessionStorage.get(sessionId);
    if (existing) {
      return existing;
    }
    const session: FactoryVisitSession = {
      id: sessionId,
      tenantId,
      factoryId: inspectionDetails.factory.id,
      inspectorId: user.id,
      sessionDate: toLocalDateString(inspectorTimeZone),
      status: 'ACTIVE',
      evidence: [],
      updatedAt: new Date().toISOString(),
    };
    await factoryVisitSessionStorage.put(session);
    setFactorySession(session);
    return session;
  };

  const removeEvidenceFromDraft = async (
    currentDraft: LocationVerificationDraft,
    clientEvidenceId: string
  ): Promise<LocationVerificationDraft | null> => {
    const evidenceToRemove = currentDraft.evidence.find(
      (item) => item.clientEvidenceId === clientEvidenceId
    );
    await locationVerificationDraftStorage.removeEvidence({
      draft: currentDraft,
      clientEvidenceId,
    });
    if (
      evidenceToRemove &&
      !evidenceToRemove.isReused &&
      factorySession &&
      inspectionDetails?.factory?.id
    ) {
      const sessionEvidenceId =
        evidenceToRemove.sourceClientEvidenceId ?? evidenceToRemove.clientEvidenceId;
      const updatedSession = await factoryVisitSessionStorage.removeCapturedEvidence({
        session: factorySession,
        clientEvidenceId: sessionEvidenceId,
      });
      setFactorySession(updatedSession);
    }
    if (!id) {
      return null;
    }
    return locationVerificationDraftStorage.get(id);
  };

  const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const enqueueCaptureSave = (task: () => Promise<void>): void => {
    captureSaveQueueRef.current = captureSaveQueueRef.current.then(task).catch(() => undefined);
  };

  const addPendingCapture = (capture: PendingVerificationCapture): boolean => {
    const current = pendingCapturesRef.current;
    const savedCount = getEvidenceForRole(draft, capture.role).filter(
      (evidence) => evidence.hasPhoto !== false
    ).length;
    const pendingCount = current.filter((item) => item.role === capture.role).length;
    if (savedCount + pendingCount >= MAX_PHOTOS_PER_LOCATION) {
      return false;
    }
    const next = [...current, capture];
    pendingCapturesRef.current = next;
    setPendingCaptures(next);
    return true;
  };

  const removePendingCapture = (clientEvidenceId: string): void => {
    const next = pendingCapturesRef.current.filter(
      (item) => item.clientEvidenceId !== clientEvidenceId
    );
    pendingCapturesRef.current = next;
    setPendingCaptures(next);
  };

  const resolveCaptureRole = (): LocationEvidenceRole =>
    step === 'room' ? 'INSPECTION_ROOM' : 'ENTRANCE';

  const canAddPhotoForRole = (role: LocationEvidenceRole): boolean => {
    const savedCount = getEvidenceForRole(draft, role).filter(
      (evidence) => evidence.hasPhoto !== false
    ).length;
    const pendingCount = pendingCapturesRef.current.filter((item) => item.role === role).length;
    return savedCount + pendingCount < MAX_PHOTOS_PER_LOCATION;
  };

  const showMaxPhotosError = (role: LocationEvidenceRole): void => {
    if (role === 'ENTRANCE') {
      toast.error(t('factory_verification_max_entrance_photos'));
    } else {
      toast.error(t('factory_verification_max_room_photos'));
    }
  };

  const persistCapturedPhoto = async (params: {
    clientEvidenceId: string;
    role: LocationEvidenceRole;
    imageData: string;
    contentType: string;
    photoCapturedAt: string;
    gpsPromise?: Promise<DeviceLocationFix | null> | null;
  }): Promise<void> => {
    const { clientEvidenceId, role, imageData, contentType, photoCapturedAt, gpsPromise } = params;
    if (!id) {
      throw new Error('Missing inspection id');
    }
    const locationFields = await resolvePhotoEvidenceLocation({
      photoCapturedAt,
      gpsPromise,
    });
    const evidence: LocationEvidence = {
      clientEvidenceId,
      role,
      latitude: locationFields.latitude,
      longitude: locationFields.longitude,
      accuracy: locationFields.accuracy,
      altitude: locationFields.altitude,
      altitudeAccuracy: locationFields.altitudeAccuracy,
      heading: locationFields.heading,
      speed: locationFields.speed,
      gpsCaptureSource: locationFields.gpsCaptureSource,
      gpsAccuracyPreciseEnough: locationFields.gpsAccuracyPreciseEnough,
      connectionType: locationFields.connectionType,
      connectionEffectiveType: locationFields.connectionEffectiveType,
      geolocationPermissionState: locationFields.geolocationPermissionState,
      clientUserAgent: locationFields.clientUserAgent,
      gpsCapturedAt: locationFields.gpsCapturedAt,
      contentType,
      localImageKey: clientEvidenceId,
      uploadState: 'PENDING',
      factoryId: inspectionDetails?.factory?.id,
      isReused: false,
      hasPhoto: true,
    };
    const baseDraft = await ensureDraft();
    const session = await ensureFactorySession();
    const serverSessionId = resolveServerFactoryVisitSessionId(session);
    if (serverSessionId) {
      evidence.factoryVisitSessionId = serverSessionId;
    }
    const nextDraft: LocationVerificationDraft = {
      ...baseDraft,
      evidence: [...baseDraft.evidence, evidence],
      workflowState:
        role === 'ENTRANCE'
          ? 'ENTRANCE_CAPTURED'
          : hasEvidenceForRole(baseDraft, 'ENTRANCE')
            ? 'ROOM_CAPTURED'
            : 'ENTRANCE_CAPTURED',
      isProgressCommitted: role === 'ENTRANCE' ? false : baseDraft.isProgressCommitted,
    };
    await locationVerificationDraftStorage.saveEvidenceCapture({
      draft: nextDraft,
      evidence,
      imageData,
    });
    if (session) {
      const updatedSession = await factoryVisitSessionStorage.addCapturedEvidence({
        session,
        evidence,
        imageData,
        sourceInspectionId: id,
      });
      setFactorySession(updatedSession);
    }
    logFactoryVerificationDebug('photo.persisted', {
      inspectionId: id,
      clientEvidenceId,
      role,
      latitude: evidence.latitude,
      longitude: evidence.longitude,
      accuracyMetres: evidence.accuracy,
      factoryVisitSessionId: evidence.factoryVisitSessionId,
      sessionUpdated: Boolean(session),
    });
    await refreshDraft();
    setThumbnailUrls((prev) => ({ ...prev, [clientEvidenceId]: imageData }));
  };

  const handleTakePhotoClick = async (): Promise<void> => {
    if (!canTakePhoto) {
      return;
    }
    const role = resolveCaptureRole();
    if (!canAddPhotoForRole(role)) {
      showMaxPhotosError(role);
      return;
    }
    const session = startPhotoGpsCapture({
      generateId: generateUniqueId,
      captureGps: resolvePhotoGpsFix,
    });
    pendingPhotoCaptureRef.current = {
      clientEvidenceId: session.clientEvidenceId,
      role,
      gpsPromise: session.gpsPromise,
    };
    cameraInputRef.current?.click();
  };

  const handleCapture = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
    if (!file || !id) {
      pendingPhotoCaptureRef.current = null;
      return;
    }
    const photoCapturedAt = new Date().toISOString();
    const pendingSession = pendingPhotoCaptureRef.current;
    pendingPhotoCaptureRef.current = null;
    if (!isImageSupported(file)) {
      toast.error(t('verification_unsupported_format'));
      return;
    }
    const role = pendingSession?.role ?? resolveCaptureRole();
    if (!canAddPhotoForRole(role)) {
      showMaxPhotosError(role);
      return;
    }
    let imageData: string;
    try {
      imageData = await readFileAsDataUrl(file);
    } catch (error) {
      Sentry.captureException(error);
      toast.error(t('inspection_start_failed'));
      return;
    }
    const clientEvidenceId = pendingSession?.clientEvidenceId ?? generateUniqueId();
    const contentType = file.type || 'image/jpeg';
    const gpsPromise = pendingSession?.gpsPromise ?? null;
    const added = addPendingCapture({
      clientEvidenceId,
      role,
      previewUrl: imageData,
    });
    if (!added) {
      showMaxPhotosError(role);
      return;
    }
    const captureId = clientEvidenceId;
    const captureRole = role;
    enqueueCaptureSave(async () => {
      try {
        await persistCapturedPhoto({
          clientEvidenceId: captureId,
          role: captureRole,
          imageData,
          contentType,
          photoCapturedAt,
          gpsPromise,
        });
        removePendingCapture(captureId);
      } catch (error) {
        Sentry.captureException(error);
        toast.error(t('inspection_start_failed'));
        removePendingCapture(captureId);
      }
    });
  };

  const handleRemoveEvidence = async (clientEvidenceId: string) => {
    if (!draft || !id) {
      return;
    }
    setIsSaving(true);
    try {
      const updatedDraft = await removeEvidenceFromDraft(draft, clientEvidenceId);
      await refreshDraft();
      if (
        (step === 'saved' || step === 'entrance') &&
        updatedDraft &&
        !hasEvidenceForRole(updatedDraft, 'ENTRANCE')
      ) {
        navigate(ROUTES.INSPECTION_VERIFICATION.replace(':id', id).replace(':step', 'entrance'), {
          replace: true,
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardVerification = async () => {
    if (!id) {
      return;
    }
    setIsSaving(true);
    try {
      const currentDraft = draft ?? (await locationVerificationDraftStorage.get(id));
      if (currentDraft) {
        let workingDraft: LocationVerificationDraft | null = currentDraft;
        const evidenceIds = [...currentDraft.evidence].map((item) => item.clientEvidenceId);
        for (const clientEvidenceId of evidenceIds) {
          if (!workingDraft) {
            break;
          }
          workingDraft = await removeEvidenceFromDraft(workingDraft, clientEvidenceId);
        }
      }
      await locationVerificationDraftStorage.remove(id);
      setThumbnailUrls({});
      await refreshDraft();
      navigate(ROUTES.INSPECTIONS, { replace: true });
    } catch (error) {
      Sentry.captureException(error);
      toast.error(t('inspection_start_failed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackToList = () => {
    navigate(ROUTES.INSPECTIONS, { replace: true });
  };

  const handleBackToEntrance = () => {
    if (!id) {
      return;
    }
    navigate(ROUTES.INSPECTION_VERIFICATION.replace(':id', id).replace(':step', 'saved'), {
      replace: true,
    });
  };

  const advanceToRoomStep = async () => {
    if (!id || !draft) {
      return;
    }
    setIsSaving(true);
    try {
      if (hasUnsavedEntranceProgress(draft)) {
        const nextDraft: LocationVerificationDraft = {
          ...draft,
          workflowState: 'ENTRANCE_CAPTURED',
          isProgressCommitted: true,
          updatedAt: new Date().toISOString(),
        };
        await locationVerificationDraftStorage.put(nextDraft);
        toast.success(t('factory_verification_saved'));
      }
      navigate(ROUTES.INSPECTION_VERIFICATION.replace(':id', id).replace(':step', 'room'), {
        replace: true,
      });
      await refreshDraft();
    } catch (error) {
      Sentry.captureException(error);
      toast.error(t('inspection_start_failed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndContinue = async () => {
    if (!id || !draft) {
      return;
    }
    if (
      shouldAdvanceToRoomOnSave({
        step,
        hasEntrancePhoto,
        isEntranceProgressCommitted,
      })
    ) {
      await advanceToRoomStep();
      return;
    }
    if ((step === 'room' || step === 'complete') && canReviewAndStart) {
      await handleStartInspection();
    }
  };

  const renderCancelButton = (className: string) => (
    <button
      type="button"
      className={className}
      disabled={isSaving || isStarting}
      onClick={() => void handleDiscardVerification()}
    >
      {t('factory_verification_cancel_discard')}
    </button>
  );

  const handleStartInspection = async () => {
    if (!id || !inspectionDetails || !draft) {
      return;
    }
    if (!hasEvidenceForRole(draft, 'ENTRANCE') || !hasEvidenceForRole(draft, 'INSPECTION_ROOM')) {
      logFactoryVerificationError('start.missingPhotos', {
        inspectionId: id,
        hasEntrance: hasEvidenceForRole(draft, 'ENTRANCE'),
        hasRoom: hasEvidenceForRole(draft, 'INSPECTION_ROOM'),
      });
      toast.error(t('factory_verification_missing_photos'));
      return;
    }
    setIsStarting(true);
    try {
      const started = await completeVerificationAndStart({
        inspection: inspectionDetails,
        draft,
        inspectionId: id,
        userId: user?.id,
        isOnline,
        isAdministrator: hasRole(ROLE.ADMINISTRATOR),
        navigate,
      });
      if (!started) {
        logFactoryVerificationError('start.failedFromVerificationPage', { inspectionId: id });
        toast.error(t('inspection_start_failed'));
      }
    } finally {
      setIsStarting(false);
    }
  };

  const visibleEntranceEvidence = useMemo(
    () => entranceEvidence.filter((evidence) => evidence.hasPhoto !== false),
    [entranceEvidence]
  );
  const visibleRoomEvidence = useMemo(
    () => roomEvidence.filter((evidence) => evidence.hasPhoto !== false),
    [roomEvidence]
  );
  const hasEntrancePhoto = hasEvidenceForRole(draft, 'ENTRANCE');
  const hasRoomPhoto = hasEvidenceForRole(draft, 'INSPECTION_ROOM');
  const displayThumbnails = useMemo(() => {
    if (step === 'room' || step === 'complete') {
      return visibleRoomEvidence.slice(0, MAX_PHOTOS_PER_LOCATION);
    }
    if (step === 'saved' || step === 'entrance') {
      return visibleEntranceEvidence.slice(0, MAX_PHOTOS_PER_LOCATION);
    }
    return [];
  }, [step, visibleEntranceEvidence, visibleRoomEvidence]);

  const displayPhotoItems = useMemo(
    () =>
      buildFactoryVerificationPhotoGridItems({
        step,
        visibleEntranceEvidence,
        visibleRoomEvidence,
        pendingCaptures,
        maxPhotos: MAX_PHOTOS_PER_LOCATION,
      }),
    [step, visibleEntranceEvidence, visibleRoomEvidence, pendingCaptures]
  );

  const canReviewAndStart = hasEntrancePhoto && hasRoomPhoto;
  const showActionFooter = step !== 'complete' || canReviewAndStart;

  const lightboxSlides: ImageZoomSlide[] = useMemo(
    () =>
      displayThumbnails
        .filter((evidence) => Boolean(thumbnailUrls[evidence.clientEvidenceId]))
        .map((evidence) => {
          const typeLabel =
            evidence.role === 'ENTRANCE'
              ? t('factory_verification_entrance_photo_alt')
              : t('factory_verification_room_photo_alt');
          const image: Image = {
            id: evidence.clientEvidenceId,
            type: InspectionImageType.VERIFICATION,
            data: thumbnailUrls[evidence.clientEvidenceId],
            contentType: evidence.contentType,
          };
          return { image, typeLabel };
        }),
    [displayThumbnails, thumbnailUrls, t]
  );

  const openPhotoLightbox = useCallback(
    (clientEvidenceId: string) => {
      const index = lightboxSlides.findIndex((slide) => slide.image.id === clientEvidenceId);
      if (index < 0) {
        return;
      }
      setLightboxInitialIndex(index);
      setLightboxOpen(true);
    },
    [lightboxSlides]
  );

  const closePhotoLightbox = useCallback(() => setLightboxOpen(false), []);

  if (!id) {
    navigate(ROUTES.INSPECTIONS);
    return null;
  }

  if (isLoading || isDraftLoading) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.loaderContainer}>
          <CircularProgress size={28} />
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.card}>
          <p className={styles.error} role="alert">
            {errorMessage}
          </p>
          <button type="button" className={actionStyles.cancelButton} onClick={handleBackToList}>
            {t('Back')}
          </button>
        </div>
      </div>
    );
  }

  const isOnRoomStep = shouldShowRoomStepBackButton(step, stepParam);
  const isGpsCaptureStep = step === 'entrance' || step === 'room';
  const isGpsRetryActive = gpsRetryState.phase === 'retrying';
  const isInitialGpsPending =
    isGpsCaptureStep &&
    (loadingPhase === 'initialGps' || !hasCompletedInitialLocationRequest) &&
    !isGpsRetryActive;
  const showMap = shouldShowVerificationMap(step, isInitialGpsPending, deviceLocation != null);
  const showMapPlaceholder = shouldShowVerificationMapPlaceholder(
    step,
    isInitialGpsPending,
    deviceLocation != null
  );
  const showPhotoThumbnails = displayPhotoItems.length > 0;
  const allowRemovePhoto = step === 'entrance' || step === 'room' || step === 'saved';
  const isEntranceProgressCommitted = hasEntrancePhoto && isVerificationProgressCommitted(draft);
  const pendingEntranceCount = countPendingCapturesByRole(pendingCaptures, 'ENTRANCE');
  const pendingRoomCount = countPendingCapturesByRole(pendingCaptures, 'INSPECTION_ROOM');
  const hasPendingCaptures = pendingCaptures.length > 0;
  const canTakeEntrancePhoto =
    (step === 'entrance' || step === 'saved') &&
    visibleEntranceEvidence.length + pendingEntranceCount < MAX_PHOTOS_PER_LOCATION;
  const canTakeRoomPhoto =
    step === 'room' && visibleRoomEvidence.length + pendingRoomCount < MAX_PHOTOS_PER_LOCATION;
  const canTakePhoto = (canTakeEntrancePhoto || canTakeRoomPhoto) && !isSaving;
  const takePhotoLabel =
    step === 'room' || visibleEntranceEvidence.length + pendingEntranceCount === 0
      ? t('verification_take_photo')
      : t('verification_take_another');
  const canSaveAndContinue =
    !hasPendingCaptures &&
    ((step === 'entrance' && hasEntrancePhoto) ||
      (step === 'saved' && isEntranceProgressCommitted) ||
      ((step === 'room' || step === 'complete') && canReviewAndStart));

  const { step1State, step2State } = resolveUserStepStates(step);
  const isUserStepOne = isFactoryVerificationUserStepOne(step);
  const primaryActionLabel = isUserStepOne
    ? t('factory_verification_save_and_continue')
    : t('factory_verification_start_inspection');

  const onPhotoSlotKeyDown = (event: KeyboardEvent<HTMLDivElement>, clientEvidenceId: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openPhotoLightbox(clientEvidenceId);
    }
  };

  return (
    <div className={styles.pageContainer}>
      <main className={styles.card}>
        <div className={styles.header}>
          <div className={styles.titleRow}>
            {isOnRoomStep && (
              <button
                type="button"
                className={styles.backButton}
                aria-label={t('factory_verification_back_to_entrance')}
                onClick={handleBackToEntrance}
              >
                <ArrowBackIcon className={styles.backButtonIcon} aria-hidden="true" />
              </button>
            )}
            <div className={styles.titleBlock}>
              <h1 className={styles.title}>{t('factory_verification_title')}</h1>
              <div className={styles.subtitleStack}>
                <p className={styles.subtitle}>{t('title_summary')}</p>
                <p className={styles.subtitle}>
                  <Trans
                    i18nKey="factory_verification_subtitle_not_arrived"
                    components={{
                      strong: <strong className={styles.subtitleEmphasis} />,
                    }}
                  />
                </p>
                <p className={styles.subtitle}>
                  <Trans
                    i18nKey={
                      isUserStepOne
                        ? 'factory_verification_subtitle_entrance_prompt'
                        : 'factory_verification_subtitle_room_prompt'
                    }
                    components={{
                      strong: <strong className={styles.subtitleEmphasis} />,
                    }}
                  />
                </p>
              </div>
            </div>
          </div>
          <FactoryVerificationStepRail step1State={step1State} step2State={step2State} />
        </div>

        <div className={styles.mediaSection}>
          {(isInitialGpsPending || isGpsRetryActive) && (
            <div className={styles.statusLoader} role="status" aria-live="polite" aria-busy="true">
              <CircularProgress size={24} aria-hidden="true" />
              <p className={styles.statusLoaderTitle}>
                {isGpsRetryActive
                  ? t('factory_verification_gps_capturing_countdown', {
                      seconds: gpsRetryState.secondsRemaining,
                      attempt: gpsRetryState.attempt,
                      maxAttempts: GPS_RETRY_MAX_ATTEMPTS,
                    })
                  : t('factory_verification_gps_capturing')}
              </p>
            </div>
          )}

          {showMapPlaceholder && (
            <div className={styles.mapSection}>
              <div className={styles.mapPlaceholder} role="alert">
                <p className={styles.mapPlaceholderTitle}>
                  {t('factory_verification_gps_unavailable_title')}
                </p>
                <p className={styles.mapPlaceholderHelp}>
                  {t('factory_verification_gps_unavailable_help')}
                </p>
                <button
                  type="button"
                  className={actionStyles.startButton}
                  disabled={isGpsRetryActive}
                  onClick={() => void handleRetryLocation()}
                >
                  {t('factory_verification_gps_retry')}
                </button>
              </div>
            </div>
          )}

          {showMap && (
            <div className={styles.mapSection}>
              <div className={styles.mapFrame}>
                <InspectionLocationMap
                  className={styles.verificationMap}
                  registered={null}
                  current={
                    deviceLocation
                      ? {
                          lat: deviceLocation.latitude,
                          lng: deviceLocation.longitude,
                        }
                      : null
                  }
                  currentAccuracyMetres={deviceLocation?.accuracyMetres ?? null}
                />
                <button
                  type="button"
                  className={styles.mapExpandBtn}
                  aria-label={t('factory_verification_expand_map')}
                  onClick={() => setMapExpandOpen(true)}
                >
                  <OpenInFullOutlinedIcon className={styles.mapExpandIcon} />
                </button>
              </div>
              {deviceLocation?.gpsCapturedAt && (
                <LocationCaptureMetaBar
                  capturedAt={deviceLocation.gpsCapturedAt}
                  accuracyMetres={deviceLocation.accuracyMetres}
                />
              )}
            </div>
          )}

          {showPhotoThumbnails && (
            <div className={styles.photoScrollArea}>
              <div className={styles.photoGrid}>
                {displayPhotoItems.map((item) => {
                  if (item.kind === 'pending') {
                    const { capture } = item;
                    const altLabel =
                      capture.role === 'ENTRANCE'
                        ? t('factory_verification_entrance_photo_alt')
                        : t('factory_verification_room_photo_alt');
                    return (
                      <div
                        key={capture.clientEvidenceId}
                        className={`${styles.photoSlot} ${styles.photoSlotPending}`}
                        aria-busy="true"
                        aria-label={t('factory_verification_photo_saving')}
                      >
                        <img
                          src={capture.previewUrl}
                          alt={altLabel}
                          className={styles.photoImage}
                        />
                        <div className={styles.pendingOverlay}>
                          <CircularProgress size={22} aria-hidden="true" />
                        </div>
                      </div>
                    );
                  }
                  const { evidence } = item;
                  const thumbnailSrc = thumbnailUrls[evidence.clientEvidenceId];
                  const canZoom = Boolean(thumbnailSrc);
                  return (
                    <div
                      key={evidence.clientEvidenceId}
                      className={`${styles.photoSlot} ${canZoom ? styles.zoomable : ''}`.trim()}
                      role={canZoom ? 'button' : undefined}
                      tabIndex={canZoom ? 0 : undefined}
                      aria-label={canZoom ? t('image_zoom_open') : undefined}
                      onClick={() => {
                        if (canZoom) {
                          openPhotoLightbox(evidence.clientEvidenceId);
                        }
                      }}
                      onKeyDown={(event) =>
                        canZoom ? onPhotoSlotKeyDown(event, evidence.clientEvidenceId) : undefined
                      }
                    >
                      <img
                        src={thumbnailSrc}
                        alt={
                          evidence.role === 'ENTRANCE'
                            ? t('factory_verification_entrance_photo_alt')
                            : t('factory_verification_room_photo_alt')
                        }
                        className={styles.photoImage}
                      />
                      {canZoom && (
                        <span className={styles.zoomHintBadge} aria-hidden="true">
                          <ZoomIn className={styles.zoomHintIcon} size={15} strokeWidth={2.25} />
                        </span>
                      )}
                      {allowRemovePhoto && (
                        <button
                          type="button"
                          className={styles.removeBtn}
                          aria-label={t('factory_verification_remove_photo')}
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleRemoveEvidence(evidence.clientEvidenceId);
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(event) => void handleCapture(event)}
        />

        {showActionFooter && (
          <div className={styles.actionFooter}>
            <button
              type="button"
              className={styles.takePhotoButton}
              disabled={!canTakePhoto}
              onClick={() => void handleTakePhotoClick()}
            >
              {takePhotoLabel}
            </button>
            <div className={styles.actionFooterRow}>
              {renderCancelButton(`${actionStyles.cancelButton} ${styles.actionFooterRowButton}`)}
              <button
                type="button"
                className={`${actionStyles.startButton} ${styles.actionFooterRowButton}`}
                disabled={!canSaveAndContinue || isSaving || isStarting}
                onClick={() => void handleSaveAndContinue()}
              >
                {isSaving || isStarting ? t('Loading') : primaryActionLabel}
              </button>
            </div>
          </div>
        )}

        {lightboxOpen && lightboxSlides.length > 0 && id && (
          <ImageZoomLightbox
            inspectionId={id}
            inspectionImageType={InspectionImageType.VERIFICATION}
            slides={lightboxSlides}
            initialIndex={lightboxInitialIndex}
            onClose={closePhotoLightbox}
          />
        )}

        {mapExpandOpen && showMap && deviceLocation && (
          <VerificationMapExpandOverlay
            current={{
              lat: deviceLocation.latitude,
              lng: deviceLocation.longitude,
            }}
            currentAccuracyMetres={deviceLocation.accuracyMetres}
            capturedAt={deviceLocation.gpsCapturedAt}
            onClose={() => setMapExpandOpen(false)}
          />
        )}
      </main>
    </div>
  );
};
